import React from "react";
import AgentWorkflow from "../components/AgentWorkflow";
import { renderAvatarBlueprint } from "../components/blueprintRenderers";
import { pickRandomAvatarSample } from "../lib/randomAvatarSamples";

const ACCENT = "#5e50f7";

const steps = [
  {
    key: "audience_offer",
    eyebrowLabel: "01 · CÉLKÖZÖNSÉG ÉS AJÁNLAT",
    question: "Kinek segítesz, és mit szeretnél eladni a számukra?",
    placeholder: "pl. szoló coachoknak 1:1 mentorprogramot",
    type: "textarea",
  },
  {
    key: "problem",
    eyebrowLabel: "02 · FŐ PROBLÉMA",
    question: "Mi a legnagyobb konkrét probléma vagy fájdalom, amit ezzel megoldasz?",
    placeholder: "pl. minden eladás csak rajtuk múlik, nincs rendszer",
    type: "textarea",
  },
  {
    key: "blocker_and_attempts",
    eyebrowLabel: "03 · ELAKADÁS ÉS PRÓBÁLKOZÁSOK",
    question: "Mi a fő elakadásuk jelenleg, és mivel próbálkoznak hiába?",
    placeholder:
      "pl. már fél éve posztolnak rendszeresen Instagramon és LinkedInen, de a jelentkezők 80%-a nem minőségi érdeklődő.",
    type: "textarea",
  },
  {
    key: "desired_result",
    eyebrowLabel: "04 · IGAZI SIKER",
    question: "Milyen eredményt akarnak valójában? Mit jelent nekik a siker a gyakorlatban?",
    placeholder: "pl. havi 3M stabil bevétel, heti 3 munkanap",
    type: "textarea",
  },
  {
    key: "person_details",
    eyebrowLabel: "05 · KIRŐL BESZÉLÜNK",
    question:
      "Mesélj még, mit tudunk még róluk? (konkrét személy vagy visszatérő típus - kor, nem, foglalkozás, helyszín, bármi)",
    placeholder: "pl. 36-42 éves nő, Budapesten, coach és tréner",
    type: "textarea",
  },
  {
    key: "how_you_help",
    eyebrowLabel: "06 · A TE SEGÍTSÉGED",
    question:
      "Te pontosan miben segítesz neki, és hogyan viszed át ebből az állapotból a kívánt eredményhez?",
    placeholder: "pl. letisztázunk egy prémium ajánlatot, felépítünk egy webinar funnelt, 90 nap alatt",
    type: "textarea",
  },
];

/**
 * Serialize both Express (fast) and Építő (guided) modes into the backend
 * AvatarInput shape. Express mode sends client-facing material via the
 * Triád (paste + URLs); Építő sends the 6 structured questions.
 */
function serializeValues(values, content) {
  const mode = content?.mode || "guided";

  if (mode === "fast") {
    const paste = (content?.writingSample || content?.paste || "").trim();
    const urls = Array.isArray(content?.urls) ? content.urls.filter(Boolean) : [];
    const lines = [];
    if (paste) lines.push(paste);
    if (urls.length > 0) {
      lines.push("", "Forrás URL-ek:");
      lines.push(...urls.map((u) => `- ${u}`));
    }
    return {
      voice_source_id: null,
      user_context: lines.join("\n"),
      audience_offer: "",
      problem: "",
      blocker_and_attempts: "",
      desired_result: "",
      person_details: "",
      how_you_help: "",
    };
  }

  return {
    voice_source_id: null,
    user_context: null,
    audience_offer: (values.audience_offer || "").trim(),
    problem: (values.problem || "").trim(),
    blocker_and_attempts: (values.blocker_and_attempts || "").trim(),
    desired_result: (values.desired_result || "").trim(),
    person_details: (values.person_details || "").trim(),
    how_you_help: (values.how_you_help || "").trim(),
  };
}

/**
 * Reverse map: backend `inputs` → contentState. Only used on hydration when
 * localStorage draft is empty (cleared cache, new browser). Recovers the
 * Express UI state (mode + writingSample) so the Beállítás nézet does not
 * show a naked mode selector after a previous Express generation.
 *
 * Detection: Express payload has `user_context` populated AND all six guided
 * fields are empty; anything else stays Építő (default mode). URLs are not
 * reverse-parsed from the "Forrás URL-ek:" block — they were merged into the
 * text on the backend anyway.
 */
function deserializeValues(inputs) {
  if (!inputs || typeof inputs !== "object") return null;
  const uc = (inputs.user_context || "").trim();
  const guidedFilled = [
    inputs.audience_offer, inputs.problem, inputs.blocker_and_attempts,
    inputs.desired_result, inputs.person_details, inputs.how_you_help,
  ].some((v) => (v || "").trim().length > 0);
  if (uc && !guidedFilled) {
    return { mode: "fast", writingSample: uc, paste: "", urls: [] };
  }
  return null;
}

export default function AvatarCraft() {
  return (
    <AgentWorkflow
      accent={ACCENT}
      agentKey="client"
      dashboardKey="client"
      blueprintName="Ideális Ügyfélprofil (Avatar)"
      saveEndpoint="/foundation/avatar/save"
      eyebrow="ALAPOZÁS · 02 · AVATAR"
      title="Avatar Varázsló"
      agentName="Avatar Varázsló"
      deliverableLabel="Avatar"
      readyTitle="Elkészült az Ideális Ügyfélprofilod."
      subtitle="6 kérdés. Egy tűpontos ideális ügyfélprofil. Marketingben azonnal használható."
      greeting="Szia! Feltérképezem az ideális ügyfeled gondolatait. Olyan mélyen fogod ismerni a vágyait, mint még soha - a rendszer pedig pontosan azokat a pontokat fogja eltalálni, amikre felkapja a fejét."
      fastLabel="Avatar Expressz"
      fastDesc="Tűpontos ügyfélprofil meglévő szövegekből"
      modePrompt=""
      guidedLabel="Avatar Építő"
      guidedDesc="Karakterépítés célzott kérdések alapján"
      steps={steps}
      eyebrowVariant="avatar"
      showContentInput={false}
      fastVariant="voice-source-triad"
      generateEndpoint="/foundation/avatar/generate"
      loadEndpoint="/foundation/avatar"
      refineEndpoint="/foundation/client/refine"
      renderBlueprint={renderAvatarBlueprint}
      pickRandomSample={pickRandomAvatarSample}
      serializeValues={serializeValues}
      deserializeValues={deserializeValues}
    />
  );
}
